import React from 'react';
import { Box } from '@mui/material';
import { DraggedPhotoOverlay } from './DraggedPhotoOverlay';

interface MultiDragOverlayProps {
  files: any[];
  isShowDescriptions: boolean;
  isSmallDisplay: boolean;
  maxVisibleCards?: number; // Option to limit visible cards (default: show all)
  cardOffset?: number; // Z-index stacking offset (default: 10)
}

export const MultiDragOverlay: React.FC<MultiDragOverlayProps> = ({
  files,
  isShowDescriptions,
  isSmallDisplay,
  maxVisibleCards = 5,
  cardOffset = 10,
}) => {
  if (!files || files.length === 0) return null;

  // Determine how many cards to actually render
  const cardsToRender = maxVisibleCards
    ? files.slice(0, maxVisibleCards)
    : files;
  const totalCards = cardsToRender.length;

  // Calculate the semicircle spread positions
  const calculateCardTransform = (index: number): React.CSSProperties => {
    if (totalCards === 1) {
      // Single card - use relative positioning so container has dimensions
      const translateX = index * 8;
      const translateY = index * 8;
      return {
        position: 'relative',
        zIndex: 1,
        transform: `translate(${translateX}px, ${translateY}px)`,
      };
    }

    // For multiple cards, create a stacked effect
    const isFirstCard = index === 0;

    // Each card is offset by 2px down and 2px right per index
    const translateX = (index + 1) * 8;
    const translateY = (index + 1) * 8;

    // Calculate z-index (cards in back have lower z-index)
    const zIndex = totalCards * cardOffset - index * cardOffset;

    if (isFirstCard) {
      // First card is on top, centered, and gives the container its dimensions
      return {
        position: 'relative',
        zIndex,
        transform: `translate(${translateX}px, ${translateY}px)`,
      };
    }

    return {
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex,
      transform: `translate(${translateX}px, ${translateY}px)`,
    };
  };

  // Container styles to ensure proper positioning
  const containerStyles: React.CSSProperties = {
    position: 'relative',
    display: 'inline-block',
    cursor: 'grabbing',
    transform: 'translate(-50%, -50%)',
  };

  // Create a bounding box that accounts for the spread
  const boundingBoxStyles: React.CSSProperties = {
    position: 'relative',
    width: 'auto',
    height: 'auto',
  };

  return (
    <Box sx={containerStyles}>
      <Box sx={boundingBoxStyles}>
        {cardsToRender.map((file, index) => (
          <Box
            key={file.MED_VW_KEY || file.MEDIA_ITEM_NUMBER}
            sx={calculateCardTransform(index)}
          >
            <DraggedPhotoOverlay
              file={file}
              isShowDescriptions={isShowDescriptions}
              isSmallDisplay={isSmallDisplay}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
